import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Check, X, Trash, Copy, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface RedeemCode {
  code: string;
  plan: string;
  duration_days: number;
  is_active: boolean;
  used_by: string | null;
  used_at: string | null;
  created_at: string;
  user_email?: string;
}

const CodeManagementPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState("create");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [codes, setCodes] = useState<RedeemCode[]>([]);
  const [newCode, setNewCode] = useState({
    code: "",
    plan: "premium",
    duration_days: 30,
    is_active: true
  });
  const [filter, setFilter] = useState({
    plan: "all",
    status: "all"
  });
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const subscriptionRef = useRef<any>(null);

  // Initial data fetch and real-time setup
  useEffect(() => {
    fetchCodes();
    
    // Set up periodic refresh every 2 minutes for redeem codes
    intervalRef.current = setInterval(() => {
      fetchCodes(true);
    }, 120000);

    // Set up real-time subscription for redeem_codes table changes
    const setupRealtimeSubscription = async () => {
      try {
        subscriptionRef.current = supabase
          .channel('redeem-codes-changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'redeem_codes'
            },
            (payload) => {
              
              // Debounce rapid changes by refreshing after a short delay
              setTimeout(() => {
                fetchCodes(true);
              }, 1000);
            }
          )
          .subscribe();
      } catch (error) {
        
      }
    };

    setupRealtimeSubscription();

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, []);

  const generateRandomCode = () => {
    const characters = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setNewCode({ ...newCode, code: result });
  };

  const createCode = async () => {
    if (!newCode.code || !newCode.plan || !newCode.duration_days) {
      toast({
        title: "Eksik Bilgi",
        description: "Lütfen tüm alanları doldurun",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('redeem_codes')
        .insert({
          code: newCode.code,
          plan: newCode.plan,
          duration_days: newCode.duration_days,
          is_active: true
        });

      if (error) throw error;

      toast({
        title: "Kod Oluşturuldu",
        description: `${newCode.code} kodu başarıyla oluşturuldu`,
      });

      // Reset form and refresh codes
      setNewCode({
        code: "",
        plan: "premium",
        duration_days: 30,
        is_active: true
      });
      fetchCodes();
    } catch (error) {
      
      toast({
        title: "Hata",
        description: "Kod oluşturulurken bir hata oluştu",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCodes = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      // Use a simpler query without the complex join that was causing issues
      const { data: codesData, error } = await supabase
        .from('redeem_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // If we have codes with used_by, fetch the corresponding user emails in a separate query
      const usedCodes = codesData?.filter(code => code.used_by) || [];
      
      if (usedCodes.length > 0) {
        const userIds = usedCodes.map(code => code.used_by);
        
        // Get user emails from users table in public schema
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, email')
          .in('id', userIds);
        
        if (userError) {
          
          toast({
            title: "Hata",
            description: `Kullanıcı verisi alınırken hata: ${userError.message}`,
            variant: "destructive"
          });
        }
        
        // Map emails to codes
        const codesWithEmail = codesData?.map(code => {
          if (code.used_by && userData) {
            const user = userData.find(u => u.id === code.used_by);
            return {
              ...code,
              user_email: user ? user.email : null
            };
          }
          return code;
        });
        
        setCodes(codesWithEmail || []);
      } else {
        setCodes(codesData || []);
      }
      
      setLastUpdated(new Date());
    } catch (error) {
      
      // Show more detailed error message
      const errorMsg = error instanceof Error ? error.message : "Bilinmeyen hata";
      toast({
        title: "Hata",
        description: `Kodlar alınırken bir hata oluştu: ${errorMsg}`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    fetchCodes(true);
  };

  const formatLastUpdated = () => {
    return lastUpdated.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const toggleCodeStatus = async (code: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('redeem_codes')
        .update({ is_active: !currentStatus })
        .eq('code', code);

      if (error) throw error;

      toast({
        title: "Durum Güncellendi",
        description: `Kod ${!currentStatus ? 'aktif' : 'pasif'} duruma getirildi`,
      });

      fetchCodes();
    } catch (error) {
      
      toast({
        title: "Hata",
        description: "Kod durumu güncellenirken bir hata oluştu",
        variant: "destructive"
      });
    }
  };

  const deleteCode = async (code: string) => {
    try {
      const { error } = await supabase
        .from('redeem_codes')
        .delete()
        .eq('code', code);

      if (error) throw error;

      toast({
        title: "Kod Silindi",
        description: "Kod başarıyla silindi",
      });

      fetchCodes();
    } catch (error) {
      
      toast({
        title: "Hata",
        description: "Kod silinirken bir hata oluştu",
        variant: "destructive"
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: "Kopyalandı",
        description: "Kod panoya kopyalandı",
      });
    });
  };

  const filteredCodes = () => {
    return codes.filter(code => {
      const planMatch = filter.plan === "all" || code.plan === filter.plan;
      let statusMatch = true;
      
      if (filter.status === "used") {
        statusMatch = !!code.used_by;
      } else if (filter.status === "unused") {
        statusMatch = !code.used_by;
      } else if (filter.status === "active") {
        statusMatch = code.is_active && !code.used_by;
      } else if (filter.status === "inactive") {
        statusMatch = !code.is_active;
      }
      
      return planMatch && statusMatch;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Kod Yönetimi</h2>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            Son güncelleme: {formatLastUpdated()}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
            disabled={loading || refreshing}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {refreshing ? 'Yeniliyor...' : 'Yenile'}
          </Button>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 mb-4">
          <TabsTrigger value="create">Kod Oluştur</TabsTrigger>
          <TabsTrigger value="list">Kodları Listele</TabsTrigger>
        </TabsList>
        
        <TabsContent value="create" className="m-0">
          <Card>
            <CardHeader>
              <CardTitle>Yeni Kod Oluştur</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1 block">Kod</label>
                  <Input 
                    value={newCode.code} 
                    onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                    placeholder="XYZ12345" 
                  />
                </div>
                <Button 
                  className="mt-6"
                  variant="outline"
                  onClick={generateRandomCode}
                >
                  Rastgele
                </Button>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Plan Tipi</label>
                <Select 
                  value={newCode.plan} 
                  onValueChange={(value) => setNewCode({ ...newCode, plan: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Plan seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="elite">Elite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Süre (Gün)</label>
                <Select 
                  value={newCode.duration_days.toString()} 
                  onValueChange={(value) => setNewCode({ ...newCode, duration_days: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Süre seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 gün</SelectItem>
                    <SelectItem value="90">90 gün</SelectItem>
                    <SelectItem value="180">180 gün</SelectItem>
                    <SelectItem value="365">365 gün</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                onClick={createCode} 
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Oluşturuluyor...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Kod Oluştur
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="list" className="m-0">
          <Card>
            <CardHeader>
              <CardTitle>Kod Listesi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                <Select 
                  value={filter.plan} 
                  onValueChange={(value) => setFilter({ ...filter, plan: value })}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Planlar</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="elite">Elite</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select 
                  value={filter.status} 
                  onValueChange={(value) => setFilter({ ...filter, status: value })}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Durum" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Durumlar</SelectItem>
                    <SelectItem value="used">Kullanılmış</SelectItem>
                    <SelectItem value="unused">Kullanılmamış</SelectItem>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="inactive">Pasif</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={handleManualRefresh}
                  disabled={loading || refreshing}
                >
                  {loading || refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </div>
              
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kod</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Süre</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Kullanıcı</TableHead>
                      <TableHead>İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-10">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : filteredCodes().length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                          Hiç kod bulunamadı
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCodes().map((code) => (
                        <TableRow key={code.code}>
                          <TableCell className="font-mono">
                            <div className="flex items-center space-x-2">
                              <span>{code.code}</span>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-5 w-5"
                                onClick={() => copyToClipboard(code.code)}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>{code.plan === 'premium' ? 'Premium' : 'Elite'}</TableCell>
                          <TableCell>{code.duration_days} gün</TableCell>
                          <TableCell>
                            {code.used_by ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-500">
                                Kullanıldı
                              </span>
                            ) : code.is_active ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-500">
                                Aktif
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-500">
                                Pasif
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {code.used_by ? (
                              <div className="text-xs">
                                <div>{code.user_email}</div>
                                <div className="text-muted-foreground mt-1">
                                  {code.used_at && format(new Date(code.used_at), 'dd.MM.yyyy HH:mm')}
                                </div>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-1">
                              {!code.used_by && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => toggleCodeStatus(code.code, code.is_active)}
                                    title={code.is_active ? "Pasif Yap" : "Aktif Yap"}
                                  >
                                    {code.is_active ? (
                                      <X className="h-4 w-4 text-red-500" />
                                    ) : (
                                      <Check className="h-4 w-4 text-green-500" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => deleteCode(code.code)}
                                    title="Sil"
                                  >
                                    <Trash className="h-4 w-4 text-red-500" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CodeManagementPanel; 